import { KEY } from '@constants/key';
import { useLocalization } from '@lib/localization';
import {
  abandonSsoWindow, openSsoWindow, redeemSsoAnswer, ssoErrorKey, SsoInsecureContextError, ssoStartUrl
} from '@lib/sso';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MuiCard from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { useLoginMutation, useSsoSignInMutation } from '@store/api/authEndpoints';
import { useGetConfigQuery } from '@store/api/rootEndpoints';
import { gray } from '@theme/primitives';
import React, { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const SignInCard = () => {
  const [usernameError, setUsernameError] = React.useState(false);
  const [usernameErrorMessage, setUsernameErrorMessage] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
  const [isRememberMeChecked, setRememberMeChecked] = useState(false);
  const [usernameFieldValue, setUsernameFieldValue] = useState('');
  const passwordTextFieldRef = useRef<HTMLInputElement>(null);

  const [login, { isLoading }] = useLoginMutation();
  const [ssoSignIn, { isLoading: isSsoSigningIn }] = useSsoSignInMutation();

  const { t } = useLocalization();
  const location = useLocation();

  // Public, and read before anybody is signed in: whether this server signs people in through
  // an identity provider, and whether a password is still everybody's.
  const { data: config } = useGetConfigQuery();
  const sso = config?.sso?.enabled ? config.sso : undefined;
  const isPasswordForSysadminOnly = sso?.passwordLogin === 'sysadmin_only';

  // With passwords left to the sysadmin, the form is one link away: it is the way in when the
  // provider is down, and nobody else's.
  const [isPasswordFormOpen, setPasswordFormOpen] = useState(false);
  const [isSsoWaiting, setSsoWaiting] = useState(false);
  // Also how the callback page says why a sign-in made in this tab was refused.
  const [ssoError, setSsoError] = useState<string | undefined>(
    (location.state as { ssoError?: string } | null)?.ssoError
  );
  const ssoAttempt = useRef(0);

  const handleSso = async () => {
    const attempt = ++ssoAttempt.current;
    // A click on the button again leaves an earlier attempt behind; only the latest speaks.
    const isCurrent = () => attempt === ssoAttempt.current;

    setSsoError(undefined);
    setSsoWaiting(true);

    try {
      const outcome = await openSsoWindow(ssoStartUrl, {
        fallback: 'redirect',
        onWindowClosed: () => { if (isCurrent()) setSsoWaiting(false); },
      });

      // Left behind by a click again, by a password sign-in, or by this page going away.
      if (outcome.kind !== 'answer' || !isCurrent()) return;

      const error = await redeemSsoAnswer(
        outcome.answer, outcome.request.verifier, (input) => ssoSignIn(input).unwrap()
      );

      if (error && isCurrent()) setSsoError(error);
    } catch (error) {
      if (isCurrent()) setSsoError(error instanceof SsoInsecureContextError ? 'insecure_context' : 'server_error');
    } finally {
      if (isCurrent()) setSsoWaiting(false);
    }
  };

  /**
   * Stops waiting for a sign-in through the provider: its answer, if it still comes, opens no
   * session. Without this a window left open replaced, minutes later, the session that a
   * password opened meanwhile, with that of whoever the provider signed in.
   */
  const leaveSso = () => {
    ssoAttempt.current += 1;
    abandonSsoWindow();
    setSsoWaiting(false);
  };

  // The page going away, once signed in or otherwise.
  useEffect(() => () => {
    ssoAttempt.current += 1;
    abandonSsoWindow();
  }, []);

  useEffect(() => {
    const username = localStorage.getItem(KEY.USERNAME);
    setRememberMeChecked(!!username);

    if (username) {
      setUsernameFieldValue(username);
      passwordTextFieldRef.current?.focus();
    }
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validateInputs()) {
      return;
    }
    leaveSso();
    try {
      const formData = new FormData(event.currentTarget);
      const username = formData.get(KEY.USERNAME)?.toString() ?? '';
      const password = formData.get(KEY.PASSWORD)?.toString() ?? '';
      await login({ username, password }).unwrap();
      if (isRememberMeChecked) localStorage.setItem(KEY.USERNAME, username);
    } catch (error) {
      const failure = error as { status?: number; data?: { errors?: { reason?: string } } } | null;

      setPasswordError(true);
      const reason = failure?.data?.errors?.reason;

      // 403 is a right password on a server that no longer takes passwords from this account,
      // or of an account single sign-on manages, or of one that is suspended.
      setPasswordErrorMessage(reason === 'account_suspended'
        ? t('SignIn_AccountSuspended')
        : reason === 'sso_only' ? t('SignIn_SsoOnly')
          : failure?.status === 403 && sso ? t('SignIn_PasswordOff') : 'Invalid username or password');
    }
  };

  const validateInputs = (): boolean => {
    const username = (document.getElementById(KEY.USERNAME) as HTMLInputElement)?.value;
    const password = (document.getElementById(KEY.PASSWORD) as HTMLInputElement)?.value;
    let isValid = true;

    if (!username || username.length < 3) {
      setUsernameError(true);
      setUsernameErrorMessage('Please enter a valid username address.');
      isValid = false;
    } else {
      setUsernameError(false);
      setUsernameErrorMessage('');
    }

    if (!password || password.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage('Password must be at least 6 characters long.');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }

    return isValid;
  };

  const handleRememberChange = (event: ChangeEvent<HTMLInputElement>) => {
    setRememberMeChecked(event.target.checked);

    if (!event.target.checked) {
      localStorage.removeItem(KEY.USERNAME);
    }
  };

  return (
    <Card variant="outlined">
      <Typography component="h1" variant="h4" sx={typographyStyles}>
        {t('SignIn')}
      </Typography>
      {sso && (
        <>
          <Button fullWidth variant="contained" onClick={() => void handleSso()} disabled={isSsoWaiting || isSsoSigningIn}>
            {isSsoWaiting || isSsoSigningIn ? t('SignIn_Sso_Waiting') : t('SignIn_Sso_Button', [sso.displayName])}
          </Button>
          {ssoError && <Alert severity="error">{t(ssoErrorKey(ssoError))}</Alert>}
          {isPasswordForSysadminOnly && !isPasswordFormOpen
            ? (
              <Button variant="text" size="small" onClick={() => setPasswordFormOpen(true)} sx={{ textTransform: 'none' }}>
                {t('SignIn_Sso_LocalAccount')}
              </Button>
            )
            : <Divider>{t('SignIn_Sso_Or')}</Divider>}
        </>
      )}
      {(!isPasswordForSysadminOnly || isPasswordFormOpen) && <Box component="form" onSubmit={handleSubmit} noValidate sx={formBoxStyles}>
        <FormControl>
          <FormLabel htmlFor={KEY.USERNAME}>{t('Common_Username')}</FormLabel>
          <TextField
            defaultValue={usernameFieldValue}
            error={usernameError}
            helperText={usernameErrorMessage}
            id={KEY.USERNAME}
            type="text"
            name="username"
            placeholder={t('SignIn_Username_Placeholder')}
            autoComplete="username"
            autoFocus
            required
            fullWidth
            variant="outlined"
            color={usernameError ? 'error' : 'primary'}
          />
        </FormControl>
        <FormControl>
          <FormLabel htmlFor={KEY.PASSWORD}>{t('Common_Password')}</FormLabel>
          <TextField
            inputRef={passwordTextFieldRef}
            error={passwordError}
            helperText={passwordErrorMessage}
            name="password"
            placeholder="••••••"
            type="password"
            id={KEY.PASSWORD}
            autoComplete="current-password"
            required
            fullWidth
            variant="outlined"
            color={passwordError ? 'error' : 'primary'}
          />
        </FormControl>
        <FormControlLabel
          control={<Checkbox
            value="remember"
            color="primary"
            checked={isRememberMeChecked}
            onChange={handleRememberChange}
          />}
          label={t('SignIn_RememberMe')}
        />
        <Button type="submit" fullWidth variant={sso ? 'outlined' : 'contained'} disabled={isLoading}>
          {isLoading ? t('SignIn_Loading') : t('SignIn')}
        </Button>
      </Box>}
    </Card>
  );
};

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  '&.MuiPaper-root': { background: '#05070A66 !important', },
  '& .MuiOutlinedInput-root': { background: `${gray[900]} !important`, },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  [theme.breakpoints.up('sm')]: {
    width: '450px',
  },
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const typographyStyles = {
  width: '100%',
  fontSize: 'clamp(2rem, 10vw, 2.15rem)',
};

const formBoxStyles = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: 2,
};

export default SignInCard;
